import React, { useEffect, useState } from 'react';
import { api, formatEUR } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Scissors, Trash2, Edit, Save } from 'lucide-react';
import { toast } from 'sonner';

function SurgeryDialog({ open, onOpenChange, initial, onSaved, templates }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => initial || {
    code: '', name: '', description: '', price: 0, price_min: '', price_max: '', anesthesia: '',
    included: [], excluded: [], consent_template_code: '', requires_co2: false, requires_lipofilling: false, active: true,
  });
  const [includedStr, setIncludedStr] = useState((initial?.included || []).join('\n'));
  const [excludedStr, setExcludedStr] = useState((initial?.excluded || []).join('\n'));
  useEffect(() => {
    if (open) {
      const i = initial || { code: '', name: '', description: '', price: 0, price_min: '', price_max: '', anesthesia: '', included: [], excluded: [], consent_template_code: '', requires_co2: false, requires_lipofilling: false, active: true };
      setForm(i);
      setIncludedStr((i.included || []).join('\n'));
      setExcludedStr((i.excluded || []).join('\n'));
    }
  }, [open, initial]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      // Phase 25.C — horquilla interna; null si no se rellena
      price_min: form.price_min === '' || form.price_min == null ? null : Number(form.price_min),
      price_max: form.price_max === '' || form.price_max == null ? null : Number(form.price_max),
      included: includedStr.split('\n').filter(Boolean),
      excluded: excludedStr.split('\n').filter(Boolean),
    };
    try {
      if (isEdit) { await api.put(`/api/surgeries/${initial.id}`, payload); toast.success('Actualizada'); }
      else { await api.post('/api/surgeries', payload); toast.success('Creada'); }
      onSaved && onSaved(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar cirugía' : 'Nueva cirugía'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Código</Label><Input required value={form.code} disabled={isEdit} onChange={(e) => setF('code', e.target.value)} placeholder="codigo-intervencion" /></div>
          <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setF('name', e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={(e) => setF('description', e.target.value)} /></div>
          <div><Label>Precio público €</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setF('price', e.target.value)} data-testid="surgery-price" /></div>
          <div><Label>Anestesia</Label><Input value={form.anesthesia || ''} onChange={(e) => setF('anesthesia', e.target.value)} placeholder="Ej.: local, sedación, general" /></div>
          {/* Phase 25.C — horquilla interna (solo equipo). No se muestra al paciente. */}
          <div className="md:col-span-2 border-t border-border pt-3 mt-1">
            <Label className="text-[#13231E] font-semibold">Horquilla interna (solo equipo)</Label>
            <p className="text-xs text-muted-foreground mb-2">Estas cifras NO aparecen en presupuestos enviados al paciente. Sirven para orientar al equipo en consulta.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Mínimo €</Label>
                <Input
                  type="number" step="0.01"
                  value={form.price_min ?? ''}
                  onChange={(e) => setF('price_min', e.target.value)}
                  placeholder="Ej. 1000"
                  data-testid="surgery-price-min"
                />
              </div>
              <div>
                <Label className="text-xs">Máximo €</Label>
                <Input
                  type="number" step="0.01"
                  value={form.price_max ?? ''}
                  onChange={(e) => setF('price_max', e.target.value)}
                  placeholder="Ej. 2000"
                  data-testid="surgery-price-max"
                />
              </div>
            </div>
          </div>
          <div className="md:col-span-2"><Label>Incluye (un ítem por línea)</Label><Textarea rows={4} value={includedStr} onChange={(e) => setIncludedStr(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>No incluye (un ítem por línea)</Label><Textarea rows={2} value={excludedStr} onChange={(e) => setExcludedStr(e.target.value)} /></div>
          {/* Servicio del PACIENTE que va en su propia línea del presupuesto
              (p. ej. retirar un relleno previo): si el evolutivo lo nombra,
              el presupuesto que nace de él lo añade aparte con este precio, y
              lo que se cobre por él no descuenta del saldo de la cirugía. */}
          <div className="md:col-span-2 border-t border-border pt-3 mt-1">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={!!form.linea_aparte}
                onChange={(e) => setF('linea_aparte', e.target.checked)} data-testid="surgery-linea-aparte" />
              <span>
                Se presupuesta en su propia línea
                <span className="block text-xs text-muted-foreground">Si el evolutivo lo nombra, sale como línea aparte con este precio y su cobro no descuenta de la cirugía.</span>
              </span>
            </label>
            {form.linea_aparte && (
              <div className="mt-2">
                <Label className="text-xs">Texto de la línea en el presupuesto (vacío = el nombre)</Label>
                <Input value={form.concepto_presupuesto || ''} onChange={(e) => setF('concepto_presupuesto', e.target.value)} data-testid="surgery-concepto-presupuesto" />
              </div>
            )}
          </div>
          <div className="md:col-span-2"><Label>Código plantilla consentimiento</Label>
            <select value={form.consent_template_code || ''} onChange={(e) => setF('consent_template_code', e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
              <option value="">—</option>
              {templates.map(t => <option key={t.code} value={t.code}>{t.name} ({t.code})</option>)}
            </select>
          </div>
          <DialogFooter className="md:col-span-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"><Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fetchItems = async () => { setLoading(true); try { const [s, t] = await Promise.all([api.get('/api/surgeries'), api.get('/api/consent-templates')]); setItems(s.data); setTemplates(t.data); } finally { setLoading(false); } };
  useEffect(() => { fetchItems(); }, []);
  const onDelete = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/api/surgeries/${id}`); toast.success('Eliminada'); fetchItems(); };
  return (
    <div>
      <PageHeader title="Catálogo de cirugías" subtitle={`${items.length} cirugías`}
        actions={<Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="catalog-new"><Plus className="w-4 h-4 mr-2" /> Nueva cirugía</Button>} />
      <div className="cp-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <EmptyState icon={Scissors} title="Sin cirugías" description="Añade la primera." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Código</th><th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Precio</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right" title="Horquilla interna - solo equipo">Horquilla</th><th className="py-3 px-4 font-semibold text-[#13231E]">Anestesia</th><th className="py-3 px-4 font-semibold text-[#13231E]">Plantilla</th><th></th></tr></thead>
            <tbody>{items.map(s => <tr key={s.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="catalog-row">
              <td className="py-3 px-4 text-xs font-mono">{s.code}</td>
              <td className="py-3 px-4 font-medium">{s.name}</td>
              <td className="py-3 px-4 text-right tabular-nums font-semibold text-[#13231E]">{formatEUR(s.price)}</td>
              <td className="py-3 px-4 text-right text-xs text-muted-foreground tabular-nums">
                {(s.price_min != null || s.price_max != null) ? (
                  <span title="Solo equipo">
                    {s.price_min != null ? formatEUR(s.price_min) : '?'} – {s.price_max != null ? formatEUR(s.price_max) : '?'}
                  </span>
                ) : '—'}
              </td>
              <td className="py-3 px-4 text-xs">{s.anesthesia}</td>
              <td className="py-3 px-4 text-xs">{s.consent_template_code || '—'}</td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid="catalog-edit"><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody>
          </table></div>}
      </div>
      <SurgeryDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} templates={templates} />
    </div>
  );
}
